import { confirmAction } from './feedback.jsx';

// app/static/js/soc/perms-ui.jsx
// The Permissions card on the settings page (spec 2026-09-07 table-permissions
// §8.2): groups, their members and tables, the Priority seed, and "what can
// this user see". The server owns every rule; this names the outcomes in two
// languages. Exposed as window.PermissionsSection.
//
// Layout is master-detail: the rail navigates (company, the group list, and
// the form that ADDS to that list), the pane edits the one selected group.
// The two were stacked in one column before, which put the empty "new group"
// form between a group and its own editor and made four identical text inputs
// read as one form. Members and Tables each carry their own named Save,
// because two anonymous full-width Save bars cannot say which is which.
const { useEffect, useState } = React;

const T = {
  en: {
    title: 'Table permissions', note: 'A group is a set of users and the mirror tables they may read. A user sees the union of their groups; a login in no group sees nothing. Grants are per table, for every company.',
    tenant: 'Company', groups: 'Groups', newGroup: 'New group', name: 'Group name', desc: 'Description', create: 'Create', rename: 'Save name', del: 'Delete group',
    confirmDel: 'Delete this group? Its members lose these tables on their next request.',
    members: 'Members', tables: 'Tables', all: 'All tables', addTable: 'Add a table…', addForm: 'Add a form (its base table)…', save: 'Save', saved: 'saved', remove: 'Remove',
    saveMembers: 'Save members', saveTables: 'Save tables',
    nMembers: (n) => `${n} ${n === 1 ? 'member' : 'members'}`, nTables: (n) => `${n} ${n === 1 ? 'table' : 'tables'}`,
    selected: (n, m) => `${n} of ${m} selected`,
    noGroups: 'No groups yet. Create the first one below.',
    pickGroup: 'Select a group to edit its members and tables.',
    pickGroupSub: 'Or create one in the rail. A login in no group sees nothing.',
    allNote: 'Every mirrored table, including tables synced later.',
    byTable: 'By table name', byForm: 'By Priority screen',
    seed: 'Seed from Priority', seedNote: 'Copies a Priority user’s form privileges (R/W/V, not X) into this group’s tables, replacing them. Not kept in sync afterwards.',
    env: 'Company (env)', plogin: 'Priority login', preview: 'Preview', apply: 'Apply', seedResult: (p) => `${p.forms} forms → ${p.tables} tables (${Object.entries(p.by_priv).map(([k, v]) => `${k}: ${v}`).join(', ')}); ${p.unmapped} forms have no synced dictionary.`,
    effective: 'What a user can see', pick: 'Pick a user', nothing: 'Nothing — this user is in no group with tables.', everything: 'Everything (a group with All tables).', via: 'via',
    sync: 'Re-sync database roles', syncNote: 'Needed after a keyring rotation or after a structure sync added tables or columns.', syncDone: (r) => r.skipped ? 'Skipped (not PostgreSQL).' : `roles +${r.roles_created.length} −${r.roles_dropped.length}, tables +${r.tables_granted} −${r.tables_revoked}, memberships +${r.memberships_granted} −${r.memberships_revoked}${r.tables_missing.length ? `; ${r.tables_missing.length} granted tables not in the schema` : ''}.`,
  },
  he: {
    title: 'הרשאות טבלאות', note: 'קבוצה היא אוסף משתמשים וטבלאות מראה שמותר להם לקרוא. משתמש רואה את איחוד הקבוצות שלו; כניסה ללא קבוצה לא רואה דבר. ההרשאה היא לטבלה, לכל החברות.',
    tenant: 'חברה', groups: 'קבוצות', newGroup: 'קבוצה חדשה', name: 'שם הקבוצה', desc: 'תיאור', create: 'צור', rename: 'שמור שם', del: 'מחק קבוצה',
    confirmDel: 'למחוק את הקבוצה? חבריה יאבדו את הטבלאות האלה בבקשה הבאה.',
    members: 'חברים', tables: 'טבלאות', all: 'כל הטבלאות', addTable: 'הוסף טבלה…', addForm: 'הוסף מסך (טבלת הבסיס שלו)…', save: 'שמור', saved: 'נשמר', remove: 'הסר',
    saveMembers: 'שמור חברים', saveTables: 'שמור טבלאות',
    nMembers: (n) => (n === 1 ? 'חבר אחד' : `${n} חברים`), nTables: (n) => (n === 1 ? 'טבלה אחת' : `${n} טבלאות`),
    selected: (n, m) => `${n} מתוך ${m} נבחרו`,
    noGroups: 'אין עדיין קבוצות. צור את הראשונה כאן למטה.',
    pickGroup: 'בחר קבוצה כדי לערוך את חבריה ואת הטבלאות שלה.',
    pickGroupSub: 'או צור אחת בסרגל. כניסה ללא קבוצה לא רואה דבר.',
    allNote: 'כל טבלאות המראה, כולל טבלאות שיסונכרנו בעתיד.',
    byTable: 'לפי שם טבלה', byForm: 'לפי מסך בפריוריטי',
    seed: 'ייבוא מפריוריטי', seedNote: 'מעתיק את הרשאות המסכים של משתמש פריוריטי (R/W/V, לא X) לטבלאות הקבוצה, במקום הקיימות. לא מסונכרן אחר כך.',
    env: 'חברה (env)', plogin: 'שם משתמש בפריוריטי', preview: 'תצוגה מקדימה', apply: 'החל', seedResult: (p) => `${p.forms} מסכים ← ${p.tables} טבלאות (${Object.entries(p.by_priv).map(([k, v]) => `${k}: ${v}`).join(', ')}); ל-${p.unmapped} מסכים אין מילון מסונכרן.`,
    effective: 'מה משתמש רואה', pick: 'בחר משתמש', nothing: 'כלום — המשתמש אינו בקבוצה עם טבלאות.', everything: 'הכול (קבוצה עם כל הטבלאות).', via: 'דרך',
    sync: 'סנכרון תפקידי בסיס הנתונים', syncNote: 'נדרש אחרי החלפת מפתח או אחרי סנכרון מבנה שהוסיף טבלאות או עמודות.', syncDone: (r) => r.skipped ? 'דולג (לא PostgreSQL).' : `תפקידים +${r.roles_created.length} −${r.roles_dropped.length}, טבלאות +${r.tables_granted} −${r.tables_revoked}, חברויות +${r.memberships_granted} −${r.memberships_revoked}${r.tables_missing.length ? `; ${r.tables_missing.length} טבלאות מוענקות אינן בסכמה` : ''}.`,
  },
};

const detailOf = (err) => (err && (typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail))) || String(err && err.message || err);
const sameList = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

function GroupPanel({ t, lang, tenantId, group, users, tableNames, onChanged, own = false }) {
  const [name, setName] = useState(group.name);
  const [desc, setDesc] = useState(group.description || '');
  const [members, setMembers] = useState(new Set(group.members.map(m => m.user_id)));
  const [all, setAll] = useState(group.tables.includes('*'));
  const [tables, setTables] = useState(group.tables.filter(x => x !== '*'));
  // The last state the server acknowledged. Each Save compares against its own
  // half of this, so a Save is offered only for a block that actually changed.
  const [base, setBase] = useState({ members: group.members.map(m => m.user_id).sort(), all: group.tables.includes('*'), tables: group.tables.filter(x => x !== '*').sort() });
  const [filter, setFilter] = useState('');
  const [formQ, setFormQ] = useState('');
  const [forms, setForms] = useState([]);
  const [seed, setSeed] = useState({ env: '', login: '', preview: null });
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const ok = (m) => { setError(''); setMsg(m); onChanged(); };
  const fail = (e) => { setMsg(''); setError(detailOf(e)); };

  useEffect(() => {
    if (!formQ.trim()) { setForms([]); return; }
    // A company admin (own) searches its own catalog with its own token;
    // sync-lookup is the installation admin's (agent seats spec §6).
    const lookup = () => (own ? PCMAdapter.getForms({ q: formQ.trim(), limit: 20 }).then(d => d.items)
                              : PCMAdapter.getSyncLookupForms(tenantId, formQ.trim(), 20).then(d => d.forms));
    const h = setTimeout(() => lookup().then(setForms).catch(() => setForms([])), 250);
    return () => clearTimeout(h);
  }, [formQ, tenantId, own]);

  const membersDirty = !sameList([...members].sort(), base.members);
  const tablesDirty = all !== base.all || (!all && !sameList([...tables].sort(), base.tables));
  const nameDirty = !!name.trim() && (name.trim() !== group.name || desc !== (group.description || ''));

  const saveMembers = () => PCMAdapter.putPermGroupMembers(group.id, [...members]).then(() => { setBase({ ...base, members: [...members].sort() }); ok(t.saved); }).catch(fail);
  const saveTables = () => PCMAdapter.putPermGroupTables(group.id, all ? ['*'] : tables).then(() => { setBase({ ...base, all, tables: [...tables].sort() }); ok(t.saved); }).catch(fail);
  const rename = () => PCMAdapter.patchPermGroup(group.id, { name: name.trim(), description: desc }).then(() => ok(t.saved)).catch(fail);
  const remove = async () => { if (await confirmAction(t.confirmDel, { lang, danger: true, confirmLabel: t.del })) PCMAdapter.deletePermGroup(group.id).then(() => ok('')).catch(fail); };
  const addTable = (x) => { if (x && !tables.includes(x)) setTables([...tables, x].sort()); };
  const runSeed = (apply) => PCMAdapter.seedPermGroup(group.id, { env: seed.env.trim(), priority_user: seed.login.trim(), apply })
    .then(p => {
      setSeed({ ...seed, preview: p });
      if (apply) {
        // The seed replaced this group's tables on the server; GroupPanel is
        // keyed on group.id (unchanged), so PermsCard re-fetching `selected`
        // does not by itself re-initialize this component's own table state.
        // Left alone, the pre-seed `tables`/`all` stay in the chips, and the
        // next Tables Save would send them and overwrite what the seed wrote.
        PCMAdapter.getPermGroup(group.id)
          .then(g => {
            const seeded = g.tables.filter(x => x !== '*');
            setAll(g.tables.includes('*')); setTables(seeded);
            setBase(b => ({ ...b, all: g.tables.includes('*'), tables: [...seeded].sort() }));
          })
          .catch(() => {});
        ok(t.seedResult(p));
      }
    }).catch(fail);
  const candidates = filter.trim() ? tableNames.filter(x => x.toLowerCase().includes(filter.trim().toLowerCase()) && !tables.includes(x)).slice(0, 30) : [];

  return (
    <section className="perm-detail">
      <header className="perm-head">
        <div className="perm-head-fields">
          <input className="set-input" dir="auto" placeholder={t.name} aria-label={t.name} value={name} onChange={e => setName(e.target.value)}/>
          <input className="set-input" dir="auto" placeholder={t.desc} aria-label={t.desc} value={desc} onChange={e => setDesc(e.target.value)}/>
        </div>
        <div className="perm-head-acts">
          <button type="button" className={nameDirty ? 'btn btn-sm primary' : 'btn btn-sm'} disabled={!nameDirty} onClick={rename}>{t.rename}</button>
          <button type="button" className="btn btn-sm danger" onClick={remove}>{t.del}</button>
        </div>
      </header>

      <div className="perm-block">
        <div className="perm-block-head">
          <h4>{t.members}</h4>
          <span className="perm-block-meta">{t.selected(users.filter(u => members.has(u.id)).length, users.length)}</span>
          <button type="button" className={membersDirty ? 'btn btn-sm primary' : 'btn btn-sm'} disabled={!membersDirty} onClick={saveMembers}>{t.saveMembers}</button>
        </div>
        <div className="perm-checks">
          {users.map(u => (
            <label key={u.id} className={members.has(u.id) ? 'perm-check on' : 'perm-check'}>
              <input type="checkbox" checked={members.has(u.id)} onChange={e => { const n = new Set(members); e.target.checked ? n.add(u.id) : n.delete(u.id); setMembers(n); }}/>
              <span dir="auto">{u.display_name || u.username}</span>
              <span className="mono muted">{u.username}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="perm-block">
        <div className="perm-block-head">
          <h4>{t.tables}</h4>
          <span className="perm-block-meta" dir="auto">{all ? t.all : t.nTables(tables.length)}</span>
          <button type="button" className={tablesDirty ? 'btn btn-sm primary' : 'btn btn-sm'} disabled={!tablesDirty} onClick={saveTables}>{t.saveTables}</button>
        </div>
        <label className="perm-check perm-all">
          <input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)}/>
          <span dir="auto">{t.all}</span>
          <span className="muted" dir="auto">{t.allNote}</span>
        </label>
        {!all && (
          <>
            {tables.length > 0 && (
              <div className="perm-chips">{tables.map(x => (
                <span key={x} className="chip st-neutral mono">{x}
                  <button type="button" className="btn link" aria-label={`${t.remove}: ${x}`} onClick={() => setTables(tables.filter(y => y !== x))}>×</button>
                </span>
              ))}</div>
            )}
            <div className="perm-finder">
              <div className="perm-field">
                <label className="set-field-label" htmlFor={`pt-${group.id}`} dir="auto">{t.byTable}</label>
                <input id={`pt-${group.id}`} className="set-input mono" dir="ltr" placeholder={t.addTable} value={filter} onChange={e => setFilter(e.target.value)}/>
              </div>
              <div className="perm-field">
                <label className="set-field-label" htmlFor={`pf-${group.id}`} dir="auto">{t.byForm}</label>
                <input id={`pf-${group.id}`} className="set-input" dir="auto" placeholder={t.addForm} value={formQ} onChange={e => setFormQ(e.target.value)}/>
              </div>
            </div>
            {(candidates.length > 0 || forms.length > 0) && (
              <div className="perm-list">
                {candidates.map(x => <button type="button" key={x} className="btn link mono" onClick={() => { addTable(x); setFilter(''); }}>{x}</button>)}
                {forms.map(f => <button type="button" key={f.ename} className="btn link" onClick={() => { addTable(`T_${f.base_table}`); setFormQ(''); }}><span dir="auto">{f.title_he || f.ename}</span> <span className="mono muted">{f.ename} → T_{f.base_table}</span></button>)}
              </div>
            )}
          </>
        )}
      </div>

      <div className="perm-block perm-seed">
        <div className="perm-block-head"><h4>{t.seed}</h4></div>
        <p className="set-note" dir="auto">{t.seedNote}</p>
        <div className="set-row">
          <input className="set-input mono" dir="ltr" placeholder={t.env} aria-label={t.env} value={seed.env} onChange={e => setSeed({ ...seed, env: e.target.value, preview: null })}/>
          <input className="set-input mono" dir="ltr" placeholder={t.plogin} aria-label={t.plogin} value={seed.login} onChange={e => setSeed({ ...seed, login: e.target.value, preview: null })}/>
          <button type="button" className="btn btn-sm" disabled={!seed.env.trim() || !seed.login.trim()} onClick={() => runSeed(false)}>{t.preview}</button>
          <button type="button" className="btn btn-sm danger" disabled={!seed.preview} onClick={() => runSeed(true)}>{t.apply}</button>
        </div>
        {seed.preview && <div className="set-note" dir="auto">{t.seedResult(seed.preview)}</div>}
      </div>

      <div className="set-status mono" role="status" dir="auto">{msg}</div>
      {error && <div role="alert" className="set-error" dir="auto">{error}</div>}
    </section>
  );
}

// `own`: a company admin's own tenant -- no company switch, and the table
// list comes from perm-groups/tables, which its token may read.
function PermsCard({ lang, tenants, tenantId, onTenant, own = false }) {
  const t = T[lang === 'he' ? 'he' : 'en'];
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState(null);      // the group detail
  const [users, setUsers] = useState([]);
  const [tableNames, setTableNames] = useState([]);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [effUser, setEffUser] = useState('');
  const [eff, setEff] = useState(null);
  const [syncMsg, setSyncMsg] = useState('');
  const [error, setError] = useState('');

  const load = () => PCMAdapter.getPermGroups(tenantId).then(d => setGroups(d.groups)).catch(e => { setGroups([]); setError(detailOf(e)); });
  useEffect(() => {
    setGroups(null); setSelected(null); setEff(null); setEffUser(''); setError('');
    load();
    PCMAdapter.getUsers(tenantId).then(d => setUsers(d.users.filter(u => u.is_active))).catch(() => setUsers([]));
    if (own) {
      PCMAdapter.getGrantableTables(tenantId).then(d => setTableNames(d.tables)).catch(() => setTableNames([]));
    } else {
      PCMAdapter.getSyncPlans(tenantId).then(d => (d.envs && d.envs[0]) ? PCMAdapter.getSyncLookupTables(tenantId, d.envs[0]) : { tables: [] })
        .then(d => setTableNames(d.tables.map(x => `T_${x.tname}`))).catch(() => setTableNames([]));
    }
  }, [tenantId]);

  const open = (id) => PCMAdapter.getPermGroup(id).then(setSelected).catch(e => setError(detailOf(e)));
  const create = () => PCMAdapter.createPermGroup({ tenant_id: tenantId, name: newName.trim(), description: newDesc.trim() }).then(g => { setNewName(''); setNewDesc(''); load(); setSelected(g); }).catch(e => setError(detailOf(e)));
  const changed = () => { load(); if (selected) PCMAdapter.getPermGroup(selected.id).then(setSelected).catch(() => setSelected(null)); };
  const showEff = (uid) => { setEffUser(uid); if (uid) PCMAdapter.getEffectiveGrants(tenantId, Number(uid)).then(setEff).catch(e => setError(detailOf(e))); else setEff(null); };
  const sync = () => PCMAdapter.syncPermRoles(tenantId).then(r => setSyncMsg(t.syncDone(r))).catch(e => setError(detailOf(e)));

  return (
    <div className="card set-section wide">
      <h3>{t.title}</h3>
      <p className="set-note" dir="auto">{t.note}</p>

      <div className="perm-layout">
        <aside className="perm-rail">
          {!own && <div className="perm-field">
            <label className="set-field-label" htmlFor="perm-tenant" dir="auto">{t.tenant}</label>
            <select id="perm-tenant" className="set-input" value={tenantId} onChange={e => onTenant(Number(e.target.value))}>
              {tenants.map(x => <option key={x.id} value={x.id}>{x.company_name}</option>)}
            </select>
          </div>}

          <div className="perm-rail-label" dir="auto">{t.groups}</div>
          <div className="perm-groups">
            {groups && groups.length === 0 && <p className="set-note" dir="auto">{t.noGroups}</p>}
            {(groups || []).map(g => (
              <button type="button" key={g.id}
                      className={selected && selected.id === g.id ? 'perm-group on' : 'perm-group'}
                      aria-current={!!(selected && selected.id === g.id)}
                      onClick={() => open(g.id)}>
                <span className="perm-group-name" dir="auto">{g.name}</span>
                <span className="perm-group-meta">
                  <span className="chip st-neutral" dir="auto">{t.nMembers(g.member_count)}</span>
                  <span className={g.is_all ? 'chip st-info' : 'chip st-neutral'} dir="auto">{g.is_all ? t.all : t.nTables(g.table_count)}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="perm-new">
            <div className="perm-rail-label" dir="auto">{t.newGroup}</div>
            <input className="set-input" dir="auto" placeholder={t.name} aria-label={t.name} value={newName} onChange={e => setNewName(e.target.value)}/>
            <input className="set-input" dir="auto" placeholder={t.desc} aria-label={t.desc} value={newDesc} onChange={e => setNewDesc(e.target.value)}/>
            <button type="button" className="btn btn-sm primary" disabled={!newName.trim()} onClick={create}>{t.create}</button>
          </div>
        </aside>

        {selected
          ? <GroupPanel key={selected.id} t={t} lang={lang} tenantId={tenantId} group={selected} users={users} tableNames={tableNames} onChanged={changed} own={own}/>
          : <section className="perm-detail perm-empty">
              <p className="perm-empty-title" dir="auto">{t.pickGroup}</p>
              <p className="set-note" dir="auto">{t.pickGroupSub}</p>
            </section>}
      </div>

      <div className="perm-foot">
        <div className="perm-foot-col">
          <h4>{t.effective}</h4>
          <select className="set-input" aria-label={t.effective} value={effUser} onChange={e => showEff(e.target.value)}>
            <option value="">{t.pick}</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
          </select>
          {eff && (eff.all ? <p className="set-note" dir="auto">{t.everything}</p>
            : eff.tables.length === 0 ? <p className="set-note" dir="auto">{t.nothing}</p>
            : eff.groups.map(g => (
                <div className="perm-eff" key={g.id}>
                  <div className="perm-eff-name" dir="auto">{t.via} <strong>{g.name}</strong></div>
                  <div className="perm-chips">{g.tables.map(x => <span key={`${g.id}-${x}`} className="chip st-neutral mono">{x}</span>)}</div>
                </div>
              )))}
        </div>
        <div className="perm-foot-col">
          <h4>{t.sync}</h4>
          <p className="set-note" dir="auto">{t.syncNote}</p>
          <div className="set-row">
            <button type="button" className="btn btn-sm" onClick={sync}>{t.sync}</button>
            <span className="set-status mono" role="status" dir="auto">{syncMsg}</span>
          </div>
        </div>
      </div>

      {error && <div role="alert" className="set-error" dir="auto">{error}</div>}
    </div>
  );
}

function PermissionsSection({ lang }) {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  useEffect(() => { PCMAdapter.getTenants().then(d => { setTenants(d.tenants); setTenantId(d.tenants[0] ? d.tenants[0].id : null); }).catch(() => setTenants([])); }, []);
  return tenantId !== null ? <PermsCard lang={lang} tenants={tenants} tenantId={tenantId} onTenant={setTenantId}/> : null;
}

window.PermissionsSection = PermissionsSection;
window.PermsCard = PermsCard;
